import type { Article } from '../types'

export const article: Article = {
  meta: {
    slug: 'romantika-u-krbu',
    title: 'Romantika u krbu: víkend ve dvou, když se venku ochladí',
    metaTitle: 'Romantický pobyt u krbu ve dvou',
    perex:
      'Venku fouká a prší, uvnitř praská oheň a nikam se nemusí. Chalupa s krbem je nejlevnější romantika, jakou znáte – stačí dřevo, deka a dva lidi, co se nikam nechystají. Poradíme, jak takový víkend vybrat a co si na něj vzít.',
    coverImage: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=1200&q=80&auto=format&fit=crop',
    coverImageAlt: 'Rozpálený krb s praskajícím ohněm v útulném dřevěném interiéru chalupy',
    publishedAt: '2026-08-08',
    author: 'Redakce Mátevolno',
    tags: ['romantika', 'pro páry', 'krb', 'podzim'],
  },
  content: `Za oknem prší šikmo, vítr ohýbá stromy a vy sedíte na zemi před krbem s hrnkem v ruce. Nikam se nejede, nic se nestihne a poprvé za dlouhou dobu vám to vůbec nevadí.

Léto má bazén a grilování, ale romantiku ve dvou dělá spíš to opačné počasí. Když je venku nevlídno, chalupa se najednou zmenší na jednu místnost, kde je teplo – a v ní jste jen vy dva.

> Oheň je jediný program, u kterého nikdo nesahá po telefonu. Vydrží hořet dýl, než vydržíte scrollovat.

## Proč krb funguje líp než jakýkoliv plán

Romantický víkend se dá pokazit programem. Rezervovaná večeře na sedmou, prohlídka na desátou, návrat do neděle – a najednou zase jen odškrtáváte položky, akorát jinde.

Krb dělá pravý opak. Nedá se uspěchat, nedá se odbýt a nedá se dělat u něj deset věcí najednou. Zabere večer celý: přiložit, počkat, dívat se. A přesně v těch pauzách se lidem povídá nejlíp.

::nabidky{facility="indoorFireplace" adult="true" beds="2" text="Prohlédnout chalupy s krbem"}

## Nejlepší sezóna je ta, kterou všichni přeskočí

Od září do dubna jsou chalupy levnější, volnějších termínů je víc a v okolí nepotkáte nikoho. Léto je o tom, kam se vejdete; zbytek roku o tom, co si vyberete.

- **Babí léto.** Rána chladná, odpoledne ještě teplá. Ideální kombinace: přes den výlet nakrátko, večer už se topí.
- **Listopad.** Nejpodceňovanější měsíc v roce. Nic se neděje, nikam se nemusí a chalupy jsou nejlevnější.
- **Advent a leden.** Tma odpoledne ve čtyři je na chalupě přednost, ne nevýhoda. Jen počítejte s tím, že termíny kolem svátků mizí nejrychleji.
- **Předjaří.** Venku ještě zima, ale den se prodlužuje. Kombinace krb plus první procházka bez čepice je nedoceněná.

## Na co se před rezervací zeptat

V inzerátu se krb objeví na jedné fotce a tím to končí. Rozdíl mezi útulným večerem a mrznutím v deset večer je přitom v detailech.

| Vyjde to dobře | Zkazí to večer |
| --- | --- |
| Dřevo je připravené a v ceně | „Dřevo si zajistěte sami" v obci 12 km daleko |
| Krb nebo kamna přímo v obývacím pokoji | Krb v místnosti, kde se nesedí |
| Vytápěná i ložnice | Teplo jen tam, kde hoří |
| Popsaný postup zatápění | Vlhké dřevo a žádné podpalovače |

Ptejte se konkrétně: **je dřevo součástí pobytu**, kolik ho je a jestli je krb hlavní zdroj tepla, nebo jen doplněk. U roubenek a starších chalup je běžné, že se topí i v kamnech – zvládne to každý, ale je dobré to vědět dopředu, ne až v podvečer.

## Co si přibalit

Nic z toho není nutné. Právě proto to funguje.

- **Něco dobrého k pití.** Láhev vína nebo čaj, který doma nepijete, protože na něj není čas.
- **Deka navíc.** U krbu se sedí na zemi, ne na gauči. Vyzkoušeno.
- **Věci na dlouhý večer.** Karty, desková hra, knížka, kterou si budete číst nahlas. Cokoliv, co nesvítí.
- **Suroviny na jednu společnou večeři.** Vaření ve dvou je polovina zážitku – a v chalupské kuchyni to jde pomaleji a líp.
- **Teplé ponožky a nepromokavou bundu.** Aby vás počasí neuvěznilo uvnitř, ale nechalo vás se rozhodnout.

## Krb, nebo vlastní wellness?

Obojí dělá totéž – zpomalí vás. Liší se v tom, kolik si toho užijete venku a kolik uvnitř.

Když chcete večery u ohně a přes den se hýbat, hledejte [ubytování s krbem](/pobyty/s-krbem). Když má být celý víkend o zahřívání, [pobyt s vířivkou](/pobyty/s-virivkou) nebo [se saunou](/pobyty/se-saunou) je jistota i v nejhorším počasí. A když se nechcete rozhodovat, nejlepší chalupy mají obojí.

## Vyjeďte, až se ochladí

Na víkend ve dvou u krbu nepotřebujete výročí ani dovolenou. Stačí, aby venku bylo ošklivo – a vy jinde než doma:

::cta{label="4" text="Prohlédnout romantické pobyty"}`,
}
